import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
  activateDeactivateTravelRecommendation,
  createTravelRecommendation,
  getAllTravelRecommendations,
  updateTravelRecommendation,
} from "../../Services/travel_recommendation";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Button, Flex, Modal, Tag } from "antd";
import AddModal from "./AddModal";
import EditModal from "./EditModal";

const columns = (showEditModal, handleToggleActive) => [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
  },
  {
    title: "Active",
    dataIndex: "active",
    key: "active",
    render: (text, record) => (
      <>
        <Tag color={record?.active ? "green" : "red"}>
          {record?.active ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Image",
    dataIndex: "full_image_url",
    key: "full_image_url",
    render: (image) => (
        image ? (
        <img
            src={image}
            alt="Travel Recommendation"
            style={{ width: 50, height: 50, borderRadius: 5 }}
        />
        ) : (
            "-"
        )
    ),
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    render: (text, record) =>
      record?.created_at ? formatDate(record.created_at) : "-",
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (text, record) =>
      record?.updated_at ? formatDate(record.updated_at) : "-",
  },
  {
    title: "Actions",
    key: "actions",
    fixed: 'right',
    render: (_, record) => (
      <Flex gap="small">
        <Button type="link" onClick={() => showEditModal(record)}>
          Edit
        </Button>
        <Button
          variant="link"
          color={record?.active ? "danger" : "primary"}
          onClick={() => handleToggleActive(record)}
        >
          {" "}
          {record?.active ? "Deactivate" : "Activate"}
        </Button>
      </Flex>
    ),
  },
];

function TravelType() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingRecord(record);
    setIsEditModalVisible(true);
  };

  
  useEffect(() => {
    setCurrentPage(1); 
  }, [searchTerm]);
  
  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["travel_type", currentPage, pageSize, searchTerm],
    queryFn: () =>
      getAllTravelRecommendations({
        skip,
        limit: pageSize,
        name: searchTerm,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const addMutation = useMutation({
    mutationFn: createTravelRecommendation,
    onSuccess: (data) => {
        if(data){
            refetch();
            setIsAddModalVisible(false);
        }
    },
  });

  const editMutation = useMutation({
    mutationFn: updateTravelRecommendation,
    onSuccess: (data) => {
        if(data){
            refetch();
            setIsEditModalVisible(false);
        }
    },
  });

  const handleAddSubmit = (values) => {
    addMutation.mutate(values);
  };

  const handleEditSubmit = (id, values) => {
    editMutation.mutate({ id: id, ...values });
  };

  const activateMutation = useMutation({
    mutationFn: (data) =>
      activateDeactivateTravelRecommendation(data.id, data.active),
    onSuccess: () => {
      queryClient.invalidateQueries(["travel_type"]); // Refetch user list after update
    },
  });

  const handleToggleActive = (travel_recommendation) => {
    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to ${
        travel_recommendation.active ? "deactivate" : "activate"
      } this preference?`,
      okText: travel_recommendation.active ? "Deactivate" : "Activate",
      cancelText: "Cancel",
      onOk: () => {
        activateMutation.mutate({
          id: travel_recommendation.id,
          active: !travel_recommendation.active, // Toggle Active Status
        });
      },
    });
  };

  return (
    <div>
      <h3>User Travel Preference Master List</h3>
      <Flex justify="space-between">
        <DebouncedInput
          placeholder="Search by Name..."
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
        />
        <Button type="primary" onClick={showAddModal}>
          Add Travel Preference Master
        </Button>
      </Flex>
      <br />
      <CustomTable
        columns={columns(showEditModal, handleToggleActive)}
        data={data?.traveler_types || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 180px)"}
      />
      <AddModal
        isModalVisible={isAddModalVisible}
        handleOk={handleAddSubmit}
        handleCancel={() => setIsAddModalVisible(false)}
        loading={addMutation.isPending}
      />
      <EditModal
        isModalVisible={isEditModalVisible}
        handleOk={handleEditSubmit}
        handleCancel={() => setIsEditModalVisible(false)}
        loading={editMutation.isPending}
        initialValues={editingRecord}
      />
    </div>
  );
}

export default TravelType;
